import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Barcode, Link2, Loader2, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Shown when a scan doesn't match any product: staff pick the product and the scanned
// barcode is attached to it, so the manufacturer's code AND the printed 99… label both scan from now on.
export default function LinkBarcodeDialog({ scan, products, onClose, onLinked }) {
  const { getAuthHeader } = useAuth();
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState('');

  const matches = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (term.length < 2) return [];
    const words = term.split(/\s+/).filter(Boolean);
    return products.filter(p => {
      const hay = `${p.name || ''} ${p.brand || ''} ${p.sku || ''} ${p.barcode || ''} ${(p.alt_barcodes || []).join(' ')}`.toLowerCase();
      return words.every(w => hay.includes(w));
    }).slice(0, 12);
  }, [q, products]);

  const link = async (p) => {
    setSaving(p.id);
    try {
      const r = await axios.post(`${API_URL}/products/${p.id}/barcodes`, { barcode: scan }, { headers: getAuthHeader() });
      toast.success(`Barcode ${scan} linked to ${p.name}`);
      onLinked(p, r.data);
      setQ('');
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not link barcode');
    } finally {
      setSaving('');
    }
  };

  return (
    <Dialog open={!!scan} onOpenChange={(o) => { if (!o) { setQ(''); onClose(); } }}>
      <DialogContent className="max-w-lg" data-testid="link-barcode-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Barcode className="w-5 h-5 text-amber-600" /> Barcode not found</DialogTitle>
          <DialogDescription>
            <span className="font-mono font-semibold text-slate-800">{scan}</span> isn't on any product yet. Find the product below and link it — both this barcode and the one already on the product will scan from now on.
          </DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type the product name or brand…" className="pl-9" data-testid="link-barcode-search" />
        </div>
        <div className="max-h-72 overflow-y-auto divide-y border rounded-lg" data-testid="link-barcode-results">
          {q.trim().length < 2 ? (
            <p className="p-4 text-sm text-slate-400">Start typing to search your products.</p>
          ) : matches.length === 0 ? (
            <p className="p-4 text-sm text-slate-400">No products match "{q}".</p>
          ) : matches.map(p => (
            <button key={p.id} onClick={() => link(p)} disabled={!!saving} className="w-full text-left px-3 py-2 hover:bg-amber-50 flex items-center justify-between gap-3" data-testid={`link-barcode-pick-${p.id}`}>
              <span className="min-w-0">
                <span className="block text-sm font-medium text-slate-800 truncate">{p.name}</span>
                <span className="block text-xs text-slate-500">{p.brand ? `${p.brand} · ` : ''}{p.barcode ? `current barcode ${p.barcode}` : 'no barcode yet'}{(p.alt_barcodes || []).length ? ` +${p.alt_barcodes.length} more` : ''} · ${Number(p.sell_price || 0).toFixed(2)}</span>
              </span>
              {saving === p.id ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : <Link2 className="w-4 h-4 text-amber-600 shrink-0" />}
            </button>
          ))}
        </div>
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => { setQ(''); onClose(); }} data-testid="link-barcode-cancel">Cancel</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
